import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { rackPhotosAPI } from '../services/api';
import { Lightbox, clock } from '../components/RackPhotoGallery';

/* PUBLIC page behind the link the WhatsApp group gets: one day's rack photos, read
   like a page of the farm's crop diary. Rendered outside the login gate (App.js)
   because most of the group has no dashboard account; the URL's token is the only
   key, and it opens this one day and nothing else.

   One bold element - the dated green masthead in the display serif. Everything else
   stays quiet: system sans, no cards, no shadows, no motion beyond opening a photo.
   Each rack leads with its first photo at full width (usually the whole rack) and
   the rest (the layers) sit in a tighter grid under it. */

const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
`;

const Masthead = styled.header`
  background: ${p => p.theme.colors.surfaceGreen};
  color: #fff;
  .in { max-width: 880px; margin: 0 auto; padding: 22px 20px 26px; }
  .brand { display: flex; align-items: center; gap: 10px; font-size: 14px; color: rgba(255, 255, 255, 0.78); }
  .brand img { width: 28px; height: 28px; border-radius: 7px; }
  h1 {
    font-family: ${p => p.theme.fonts.display};
    font-weight: 500;
    font-size: clamp(34px, 9vw, 56px);
    line-height: 1.02;
    letter-spacing: -0.015em;
    margin: 22px 0 10px;
  }
  .sum { font-size: 16px; line-height: 1.45; color: rgba(255, 255, 255, 0.86); max-width: 34em; margin: 0; }
`;

/* Rack index: sticky, so a long day stays navigable on a phone. */
const Index = styled.nav`
  position: sticky; top: 0; z-index: 5;
  background: ${p => p.theme.colors.background};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  .in { max-width: 880px; margin: 0 auto; padding: 10px 20px; display: flex; gap: 18px; overflow-x: auto; scrollbar-width: none; }
  .in::-webkit-scrollbar { display: none; }
  a {
    flex: none; text-decoration: none; color: ${p => p.theme.colors.textSecondary};
    font-size: 15px; padding: 6px 0; border-bottom: 2px solid transparent;
  }
  a:hover, a:focus-visible { color: ${p => p.theme.colors.text}; border-bottom-color: ${p => p.theme.colors.accent}; outline: none; }
  a span { color: ${p => p.theme.colors.textMuted}; margin-left: 5px; font-variant-numeric: tabular-nums; }
`;

const Main = styled.main`
  max-width: 880px; margin: 0 auto; padding: 8px 20px 56px;
  @media (max-width: 600px) { padding: 4px 12px 44px; }
`;

const Rack = styled.section`
  scroll-margin-top: 56px;
  padding-top: 30px;
  & + & { border-top: 1px solid ${p => p.theme.colors.border}; margin-top: 34px; }
  header { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
  h2 {
    font-family: ${p => p.theme.fonts.display};
    font-weight: 500; font-size: 30px; line-height: 1.1; margin: 0; letter-spacing: -0.01em;
  }
  .meta { color: ${p => p.theme.colors.textMuted}; font-size: 14px; font-variant-numeric: tabular-nums; }
  blockquote {
    margin: 0 0 14px; padding-left: 12px;
    border-left: 3px solid ${p => p.theme.colors.accent};
    color: ${p => p.theme.colors.textSecondary}; font-size: 15px; line-height: 1.45; max-width: 60ch;
  }
`;

const Shot = styled.button`
  display: block; width: 100%; padding: 0; border: 0; cursor: zoom-in; overflow: hidden;
  background: ${p => p.theme.colors.surfaceHover};
  border-radius: ${p => (p.$lead ? '10px' : '6px')};
  aspect-ratio: ${p => (p.$lead ? '4 / 3' : '1')};
  img { width: 100%; height: 100%; object-fit: cover; display: block; opacity: 0; }
  img.in { opacity: 1; }
  /* A photo arriving is the one motion on this page: it answers the wait. */
  @media (prefers-reduced-motion: no-preference) { img { transition: opacity 0.25s ease; } }
  &:focus-visible { outline: 3px solid ${p => p.theme.colors.accent}; outline-offset: 2px; }
`;

const Layers = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; margin-top: 6px;
  @media (max-width: 600px) { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 4px; margin-top: 4px; }
`;

/* Same shape as the loading screen in public/index.html, so the hand-over from the
   pre-JavaScript skeleton to this one does not flash. */
const Skel = styled.div`
  .bar { border-radius: 6px; background: rgba(255, 255, 255, 0.16); }
  .box { border-radius: 6px; background: ${p => p.theme.colors.surfaceHover}; }
  @media (prefers-reduced-motion: no-preference) {
    .bar, .box { animation: nkPulse 1.4s ease-in-out infinite; }
    @keyframes nkPulse { 50% { opacity: 0.55; } }
  }
`;

const State = styled.div`
  max-width: 880px; margin: 0 auto; padding: 56px 20px; font-size: 16px; line-height: 1.5;
  color: ${p => p.theme.colors.textSecondary};
`;

const Foot = styled.footer`
  max-width: 880px; margin: 0 auto; padding: 0 20px 36px; font-size: 13px; color: ${p => p.theme.colors.textMuted};
`;

const longDate = day => new Date(`${day}T00:00:00+05:30`).toLocaleDateString('en-IN', {
  weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Asia/Kolkata',
});

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// The page sends every size and lets the phone pick: 640 tiles, 1280 on most phones,
// 2048 only for 3x screens. A full-width shot on a 390 px phone no longer pulls 2048 px.
const srcSet = p => `${rackPhotosAPI.src(p.thumb)} 640w, ${rackPhotosAPI.src(p.medium)} 1280w, ${rackPhotosAPI.src(p.url)} 2048w`;

function Photo({ photo, lead, sizes }) {
  const [shown, setShown] = useState(false);
  return (
    <img className={shown ? 'in' : ''} alt="" decoding="async"
      loading={lead ? 'eager' : 'lazy'} fetchpriority={lead ? 'high' : 'auto'}
      src={rackPhotosAPI.src(lead ? photo.medium : photo.thumb)}
      srcSet={photo.medium ? srcSet(photo) : undefined} sizes={sizes}
      onLoad={() => setShown(true)} onError={() => setShown(true)} />
  );
}

// Started in public/index.html before this bundle had even downloaded; fall back to
// fetching here (dev server, or the page reached some other way).
function loadShare(token) {
  const early = window.__NK_SHARE__;
  // Kept, not consumed: React may run this effect twice (StrictMode), and the second
  // run must reuse the same request rather than fetch again.
  if (early && early.token === token) return early.data;
  return rackPhotosAPI.shared(token).then(r => r.data);
}

function summary(data) {
  const days = [...new Set(data.racks.map(r => r.crop_day).filter(d => d != null))];
  const photos = `${plural(data.count, 'photo')} from ${plural(data.racks.length, 'rack')}`;
  if (days.length === 1) return `Day ${days[0]} of the crop. ${photos[0].toUpperCase()}${photos.slice(1)}.`;
  return `${photos[0].toUpperCase()}${photos.slice(1)}.`;
}

function timeSpan(photos) {
  const a = clock(photos[0].uploaded_at);
  const b = clock(photos[photos.length - 1].uploaded_at);
  return a === b ? a : `${a} to ${b}`;
}

export default function SharedPhotos({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    loadShare(token)
      .then(d => { setData(d); document.title = `Rack photos, ${longDate(d.day)}`; })
      .catch(e => setError((e.status || e.response?.status) === 404
        ? 'This link does not open any photos. It may have been copied incompletely, or the photos were deleted.'
        : 'The photos did not load. Check the connection and reload the page.'));
  }, [token]);

  const open = (rack, i) => setViewer({ photos: rack.photos, index: i, title: `${rack.name}, ${longDate(data.day)}` });

  return (
    <Page>
      <Masthead>
        <div className="in">
          <div className="brand"><img src="/logo.jpg" alt="" />NeuralKissan rack photos</div>
          {data && <h1>{longDate(data.day)}</h1>}
          {data && <p className="sum">{summary(data)}</p>}
          {error && <h1>Rack photos</h1>}
          {!data && !error && (
            <Skel aria-hidden="true">
              <div className="bar" style={{ height: 44, width: '74%', margin: '24px 0 14px' }} />
              <div className="bar" style={{ height: 16, width: '46%' }} />
            </Skel>
          )}
        </div>
      </Masthead>

      {data?.racks.length > 1 && (
        <Index aria-label="Racks">
          <div className="in">
            {data.racks.map(r => <a key={r.unit_id} href={`#${r.unit_id}`}>{r.name}<span>{r.photos.length}</span></a>)}
          </div>
        </Index>
      )}

      {!data && !error && (
        <Main aria-busy="true">
          <Skel aria-hidden="true" style={{ paddingTop: 30 }}>
            <div className="box" style={{ height: 26, width: '34%', marginBottom: 14 }} />
            <div className="box" style={{ aspectRatio: '4 / 3', borderRadius: 10 }} />
            <Layers>{[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="box" style={{ aspectRatio: '1' }} />)}</Layers>
          </Skel>
          <State style={{ padding: '14px 0 0', fontSize: 14 }}>Loading today's rack photos…</State>
        </Main>
      )}
      {error && <State role="alert">{error}</State>}
      {data?.count === 0 && <State>No photos were kept for this day.</State>}

      {data?.count > 0 && (
        <Main>
          {data.racks.map(r => {
            const [lead, ...rest] = r.photos;
            const notes = [...new Set(r.photos.map(p => p.note).filter(Boolean))];
            return (
              <Rack key={r.unit_id} id={r.unit_id} aria-labelledby={`h-${r.unit_id}`}>
                <header>
                  <h2 id={`h-${r.unit_id}`}>{r.name}</h2>
                  <span className="meta">
                    {r.crop_day != null && data.racks.some(x => x.crop_day !== r.crop_day) ? `Day ${r.crop_day}, ` : ''}
                    {plural(r.photos.length, 'photo')}, {timeSpan(r.photos)}
                  </span>
                </header>
                {notes.map(n => <blockquote key={n}>{n}</blockquote>)}
                <Shot $lead type="button" onClick={() => open(r, 0)} aria-label={`${r.name}, photo 1 of ${r.photos.length}`}>
                  <Photo photo={lead} lead sizes="(max-width: 880px) 100vw, 840px" />
                </Shot>
                {rest.length > 0 && (
                  <Layers>
                    {rest.map((p, i) => (
                      <Shot key={p.id} type="button" onClick={() => open(r, i + 1)} aria-label={`${r.name}, photo ${i + 2} of ${r.photos.length}`}>
                        <Photo photo={p} sizes="(max-width: 600px) 33vw, 210px" />
                      </Shot>
                    ))}
                  </Layers>
                )}
              </Rack>
            );
          })}
        </Main>
      )}

      {data?.count > 0 && <Foot>Tap any photo to see it full size. Swipe to move between a rack's photos.</Foot>}

      {viewer && (
        <Lightbox photos={viewer.photos} index={viewer.index} title={viewer.title}
          onIndex={i => setViewer(v => ({ ...v, index: i }))} onClose={() => setViewer(null)} />
      )}
    </Page>
  );
}
